import { z } from "zod";
import { ownerNudgeKindSchema, type BookingFor } from "../enums.ts";
import { clientRowSchema } from "./clients.ts";
import { filsSchema, isoDateSchema, isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * The owner Home (2026-10-04): five numbers, and under each one the people behind it.
 *
 *   participants  how many, who joined and who left this month
 *   came          who came last week, the six weeks before it, and who did not come
 *   booked        seats booked this week, day by day
 *   quiet         came before, has sessions left, nothing booked, not seen in 14 days
 *   runningOut    two sessions left or fewer, with what is owed (members book for their children)
 *   renewals      the same list where clients book for themselves, with the reason (Studio Home H3)
 *   studio        where clients book for themselves, the five studio numbers (Studio Home H4)
 *
 * "Came" is read from bookings: a confirmed booking in a session that started, not marked absent.
 * A business that never marks attendance still gets a true number of who was booked and turned up
 * as far as Teamna knows; a staff "absent" mark takes the person out.
 */

export const ownerHomeAdultSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  phoneE164: z.string().nullable(),
  /** In the app, so a message from Home reaches them. */
  joined: z.boolean(),
});
export type OwnerHomeAdult = z.infer<typeof ownerHomeAdultSchema>;

export const ownerHomePersonSchema = z.object({
  participantId: uuidSchema,
  /** The family account: messages and payments go to it. */
  familyId: uuidSchema,
  name: z.string(),
  age: z.number().int().nullable(),
  groupId: uuidSchema.nullable(),
  groupName: z.string().nullable(),
  groupColor: z.string().nullable(),
  staffName: z.string().nullable(),
  /** The group's staff member, for "Message the coach". */
  staff: z.object({ userId: uuidSchema, name: z.string(), phoneE164: z.string().nullable() }).nullable(),
  sessionsLeft: z.number().int(),
  lastCameAt: isoDateTimeSchema.nullable(),
  /** Sessions came in the last four weeks, and how many were booked. */
  came4Weeks: z.number().int(),
  booked4Weeks: z.number().int(),
  cameLastWeek: z.boolean(),
  joinedAt: isoDateTimeSchema,
  adults: z.array(ownerHomeAdultSchema),
  /** The profile photo (signed, expires in 12 to 24 hours; never store it). Optional for older API builds. */
  photoUrl: z.string().nullable().optional(),
});
export type OwnerHomePerson = z.infer<typeof ownerHomePersonSchema>;

export const ownerHomeLeftSchema = z.object({
  participantId: uuidSchema,
  familyId: uuidSchema,
  name: z.string(),
  groupName: z.string().nullable(),
  staff: z.object({ userId: uuidSchema, name: z.string(), phoneE164: z.string().nullable() }).nullable(),
  leftAt: isoDateTimeSchema,
  adults: z.array(ownerHomeAdultSchema),
  /** The profile photo (signed, expires in 12 to 24 hours; never store it). Optional for older API builds. */
  photoUrl: z.string().nullable().optional(),
});
export type OwnerHomeLeft = z.infer<typeof ownerHomeLeftSchema>;

/**
 * A name a coach added that waits for a parent (no participant row yet). It counts in the total and
 * in Came, as on the Swimmers page. No message can go to it: the owner links a parent first.
 */
export const ownerHomeWaitingSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  groupId: uuidSchema,
  groupName: z.string(),
  groupColor: z.string().nullable(),
  lastCameAt: isoDateTimeSchema.nullable(),
  cameLastWeek: z.boolean(),
});
export type OwnerHomeWaiting = z.infer<typeof ownerHomeWaitingSchema>;

export const ownerHomeWeekSchema = z.object({
  /** The first day of the business week (Saturday), in the business timezone. */
  weekStart: isoDateSchema,
  came: z.number().int(),
});

export const ownerHomeGroupAttendanceSchema = z.object({
  groupId: uuidSchema,
  groupName: z.string(),
  groupColor: z.string().nullable(),
  came: z.number().int(),
  total: z.number().int(),
});

export const ownerHomeSessionSchema = z.object({
  id: uuidSchema,
  groupId: uuidSchema,
  groupName: z.string(),
  groupColor: z.string().nullable(),
  startsAt: isoDateTimeSchema,
  staffName: z.string().nullable(),
  capacity: z.number().int().nullable(),
  booked: z.number().int(),
  /** People on the waiting list (H1). Optional for older API builds. */
  waitlistCount: z.number().int().min(0).optional(),
});
export type OwnerHomeSession = z.infer<typeof ownerHomeSessionSchema>;

export const ownerHomeNudgeSchema = z.object({
  id: uuidSchema,
  kind: ownerNudgeKindSchema,
  sentAt: isoDateTimeSchema,
});
export type OwnerHomeNudge = z.infer<typeof ownerHomeNudgeSchema>;

export const ownerHomeOwedSchema = z.object({
  purchaseId: uuidSchema,
  packageName: z.string(),
  priceFils: filsSchema,
  /** Received so far in part payments. */
  paidFils: filsSchema,
  /** Still to pay: the price less the part payments. */
  dueFils: filsSchema,
});
export type OwnerHomeOwed = z.infer<typeof ownerHomeOwedSchema>;

export const ownerHomeRenewalSchema = z.object({
  productId: uuidSchema,
  productName: z.string(),
  priceFils: filsSchema,
  credits: z.number().int(),
});
export type OwnerHomeRenewal = z.infer<typeof ownerHomeRenewalSchema>;

export const ownerHomeRunningOutSchema = z.object({
  person: ownerHomePersonSchema,
  owed: ownerHomeOwedSchema.nullable(),
  /** The package to sell next: the one the participant has now, while it is on sale. */
  renewal: ownerHomeRenewalSchema.nullable(),
  nudge: ownerHomeNudgeSchema.nullable(),
  /**
   * Owed sessions of the family that no package paid yet (Came with no sessions left, 2026-10-29):
   * the next package or membership month pays them. Left out means none.
   */
  owedSessions: z.number().int().min(0).optional(),
});
export type OwnerHomeRunningOut = z.infer<typeof ownerHomeRunningOutSchema>;

/**
 * Why a client is on the Renewals list (Studio Home H3). The rule is `renewalReason` in
 * `packages/shared/src/renewals.ts`:
 *
 *   low_sessions          2 sessions left or fewer
 *   ends_soon             more sessions left, but the package ends in 10 days or fewer
 *   intro_not_converted   the newest package is an intro offer, and it runs out or ends
 */
export const renewalReasons = ["low_sessions", "ends_soon", "intro_not_converted"] as const;
export const renewalReasonSchema = z.enum(renewalReasons);
export type RenewalReason = z.infer<typeof renewalReasonSchema>;

/** A row of the Renewals list: the Running out row, the reason, and the end date of the newest package. */
export const ownerHomeRenewalRowSchema = ownerHomeRunningOutSchema.extend({
  reason: renewalReasonSchema,
  /** The last day of the newest package, in the business timezone. */
  endsOn: isoDateSchema,
});
export type OwnerHomeRenewalRow = z.infer<typeof ownerHomeRenewalRowSchema>;

/** A client in a studio number (H4): the facts of the Clients table row (C1) that the panel shows. */
export const ownerHomeStudioClientSchema = clientRowSchema.pick({
  familyId: true,
  participantId: true,
  name: true,
  packageName: true,
  sessionsLeft: true,
  endsOn: true,
  nextBookingAt: true,
  intro: true,
  paused: true,
  owed: true,
});
export type OwnerHomeStudioClient = z.infer<typeof ownerHomeStudioClientSchema>;

/** A booking cancelled after the cut-off this week (H4). */
export const ownerHomeLateCancelSchema = z.object({
  bookingId: uuidSchema,
  familyId: uuidSchema,
  participantId: uuidSchema.nullable(),
  name: z.string(),
  groupName: z.string(),
  groupColor: z.string().nullable(),
  startsAt: isoDateTimeSchema,
  cancelledAt: isoDateTimeSchema,
});
export type OwnerHomeLateCancel = z.infer<typeof ownerHomeLateCancelSchema>;

/** A client who bought an intro offer in the window (H4), and whether a full package came after it. */
export const ownerHomeIntroClientSchema = z.object({
  familyId: uuidSchema,
  participantId: uuidSchema.nullable(),
  name: z.string(),
  /** The day the intro offer was bought, in the business timezone. */
  introOn: isoDateSchema,
  /** The name of the first full package bought after the intro, or null. */
  packageName: z.string().nullable(),
});
export type OwnerHomeIntroClient = z.infer<typeof ownerHomeIntroClientSchema>;

/**
 * The five studio numbers (Studio Home H4), where clients book for themselves. The week is Monday
 * to Sunday in the business timezone.
 *
 *   fill         places booked of the places in the group sessions of this week
 *   active       the Clients filter "Active" (C1): a current package, or a next booking
 *   endingSoon   the Clients filter "Ending soon" (C1): ends in 14 days or fewer, or 2 sessions left or fewer
 *   intro        of the clients who bought an intro offer in the last 90 days, who bought a full package after it
 *   lateCancels  bookings cancelled after the cut-off, with the cancel in this week
 */
export const ownerHomeStudioSchema = z.object({
  week: z.object({ from: isoDateSchema, to: isoDateSchema }),
  fill: z.object({ booked: z.number().int(), seats: z.number().int(), sessions: z.array(ownerHomeSessionSchema) }),
  active: z.array(ownerHomeStudioClientSchema),
  endingSoon: z.array(ownerHomeStudioClientSchema),
  intro: z.object({ windowDays: z.number().int(), clients: z.array(ownerHomeIntroClientSchema) }),
  lateCancels: z.array(ownerHomeLateCancelSchema),
});
export type OwnerHomeStudio = z.infer<typeof ownerHomeStudioSchema>;

export const ownerHomeQuietSchema = z.object({
  person: ownerHomePersonSchema,
  daysSince: z.number().int(),
  /** A "we miss you" message in the last 10 days. While it is there, the person is not counted. */
  nudge: ownerHomeNudgeSchema.nullable(),
});
export type OwnerHomeQuiet = z.infer<typeof ownerHomeQuietSchema>;

export const ownerHomeSchema = z.object({
  today: z.object({
    date: isoDateSchema,
    sessions: z.number().int(),
    staffOn: z.number().int(),
  }),
  participants: z.object({
    all: z.array(ownerHomePersonSchema),
    groups: z.number().int(),
    joinedThisMonth: z.array(ownerHomePersonSchema),
    leftThisMonth: z.array(ownerHomeLeftSchema),
    /** The names that wait for a parent: `all` has only participants with a parent. Optional for older API builds. */
    waiting: z.array(ownerHomeWaitingSchema).optional(),
  }),
  came: z.object({
    lastWeek: z.number().int(),
    weekBefore: z.number().int(),
    /** Oldest first, ending with last week. */
    weeks: z.array(ownerHomeWeekSchema),
    byGroup: z.array(ownerHomeGroupAttendanceSchema),
    /** People in a group who did not come last week. The names with no parent who did not come are in `participants.waiting`. */
    absent: z.array(ownerHomePersonSchema),
  }),
  booked: z.object({
    weekStart: isoDateSchema,
    booked: z.number().int(),
    seats: z.number().int(),
    sessions: z.array(ownerHomeSessionSchema),
    /** Every scheduled session this week, private ones too (`sessions` has group sessions only). Optional for older API builds. */
    allSessions: z.number().int().optional(),
  }),
  quiet: z.array(ownerHomeQuietSchema),
  /** Members book for their children: two sessions left or fewer. Empty where clients book for themselves. */
  runningOut: z.array(ownerHomeRunningOutSchema),
  /**
   * Where clients book for themselves, the Renewals list replaces Running out (Studio Home H3).
   * Absent where members book for their children, and from older API builds.
   */
  renewals: z.array(ownerHomeRenewalRowSchema).optional(),
  /** Where clients book for themselves: the five studio numbers (H4). Absent for a family business. */
  studio: ownerHomeStudioSchema.optional(),
  /**
   * Everything overdue, for every family: unpaid packages less part payments, and pay links past
   * their due date. The same rule as the AI Assistant's payments_overdue (decision 2026-10-23).
   * `runningOut` shows only the families running out. Optional for older API builds.
   */
  overdue: z.object({ totalFils: filsSchema, families: z.number().int() }).optional(),
  /** True while WhatsApp is not connected. Messages from Home go in the Teamna app either way. */
  whatsappConnected: z.boolean(),
});
export type OwnerHome = z.infer<typeof ownerHomeSchema>;

/** `POST /business/businesses/:id/nudges`: one message or several, from Home, into each family's thread. */
export const sendNudgesRequestSchema = z.object({
  items: z
    .array(z.object({ participantId: uuidSchema, kind: ownerNudgeKindSchema }))
    .min(1)
    .max(200),
});
export type SendNudgesRequest = z.infer<typeof sendNudgesRequestSchema>;

export const sendNudgesResponseSchema = z.object({
  sent: z.array(
    z.object({
      participantId: uuidSchema,
      kind: ownerNudgeKindSchema,
      nudgeId: uuidSchema,
      /** The row was on the Renewals list (H3): why. */
      reason: renewalReasonSchema.optional(),
    }),
  ),
  /** Families not in the app yet: nothing was sent to them. */
  notJoined: z.array(uuidSchema),
  /** The screen was out of date (paid, booked, came back): nothing was sent. Optional for older builds. */
  changed: z.array(uuidSchema).default([]),
});
export type SendNudgesResponse = z.infer<typeof sendNudgesResponseSchema>;

/**
 * The message text for each kind, so the screen can show what the family reads before it is sent.
 *
 * The shape decides who the message speaks about (F2): where members book for their children it
 * names the children, where clients book for themselves it speaks to the client ("We miss you").
 * A renewal from the Renewals list (H3) has its reason, and the words follow it.
 */
export function nudgeMessage(
  kind: z.infer<typeof ownerNudgeKindSchema>,
  input: {
    childName: string;
    businessName: string;
    sessionsLeft: number;
    dueText?: string;
    packageName?: string;
    bookingFor: BookingFor;
    /** Why the client is on the Renewals list. */
    reason?: RenewalReason;
    /** The last day of the package, as words ("Fri 30 Oct"), for `ends_soon`. */
    endsText?: string;
  },
): string {
  const sessions = `${input.sessionsLeft} ${input.sessionsLeft === 1 ? "session" : "sessions"}`;
  const pack = `package${input.packageName ? `, ${input.packageName},` : ""}`;
  if (input.bookingFor === "self") {
    switch (kind) {
      case "quiet":
        return `We miss you at ${input.businessName}. Is everything OK? Book a session in the app when you are ready.`;
      case "pay_reminder":
        return `${input.dueText ?? "A payment"} for your ${pack} is overdue. Please pay in the app or at the desk. Thank you.`;
      case "renewal":
        return renewalText(input, sessions);
    }
  }
  // "Zaid Saeed and Huda Saeed" (a family on one balance) reads "Zaid and Huda".
  const names = input.childName.split(" and ").map((name) => name.trim().split(/\s+/)[0] || name);
  const child = names.join(" and ");
  switch (kind) {
    case "quiet":
      return `We miss ${child} at ${input.businessName}. Is everything OK? Book a session in the app when you are ready.`;
    case "pay_reminder":
      return `${input.dueText ?? "A payment"} for ${child}'s ${pack} is overdue. Please pay in the app or at the desk. Thank you.`;
    case "renewal":
      return `${child} ${names.length > 1 ? "have" : "has"} ${sessions} left. Buy the next package in the app or at the desk to keep booking.`;
  }
}

/** A renewal to a client who books for themselves, in the words of its reason. */
function renewalText(input: { businessName: string; sessionsLeft: number; reason?: RenewalReason; endsText?: string }, sessions: string): string {
  const where = input.businessName;
  switch (input.reason) {
    case "low_sessions":
      return `You have ${sessions} left at ${where}. Buy your next package in the app or at the desk to keep booking.`;
    case "ends_soon":
      return `Your package at ${where} ends${input.endsText ? ` on ${input.endsText}` : " soon"}. You have ${sessions} left. Book them before that day, and buy your next package in the app or at the desk.`;
    case "intro_not_converted":
      return input.sessionsLeft > 0
        ? `You have ${sessions} left of your intro offer at ${where}. To keep coming, buy a package in the app or at the desk.`
        : `Thank you for coming to ${where}. Your intro offer is used. To keep coming, buy a package in the app or at the desk.`;
    default:
      return `You have ${sessions} left. Buy the next package in the app or at the desk to keep booking.`;
  }
}

/**
 * The owner's To do list on Home (concept D of the strategy repo `teamna/prototypes/owner-answers.html`,
 * 2026-10-16). One list of every job that waits for the owner, so no job hides under the numbers.
 *
 * The list is generic: each kind is one source of jobs. To add a job, add a kind here, a source in
 * `packages/core/src/services/owner-todo.ts`, and a row in `packages/ui/src/owner/todo/`. The server
 * sends only what it counts; the screen writes the words in the business's own labels.
 *
 *   parent_answers           parents who answered the find link (`/find/:code`), not checked yet
 *   pending_members          people who joined with the code and are not on the list
 *   sessions_to_decide       someone came and was not booked: take a session, or let it go
 *   participants_to_confirm  a member added a participant: confirm it and its group
 *   link_parents             names coaches added that still have no parent
 *   plan_next_month          the last planned session is 5 days away or less, or past, and the
 *                            month after has nothing planned
 *   cover_requests           a staff member said yes to a cover, and the owner approves each cover
 *   fees_to_confirm          a no-show or late-cancel fee waits: the owner sends it or waives it
 */
export const ownerTodoKindSchema = z.enum([
  "parent_answers",
  "pending_members",
  "sessions_to_decide",
  "participants_to_confirm",
  "link_parents",
  /** The last planned session is 5 days away or less, or past, and the month after has nothing planned. */
  "plan_next_month",
  /** A staff member said yes to a cover request, and "Approve each cover" is on (migration 0072). */
  "cover_requests",
  /** A no-show or late-cancel fee is final and waits for the owner: send or waive (migration 0078). */
  "fees_to_confirm",
  /** Came took a session from a paused package (migration 0087): end the pause, or keep it. */
  "came_during_pause",
]);
export type OwnerTodoKind = z.infer<typeof ownerTodoKindSchema>;

/** The jobs the front desk sees on its To do list (migration 0080, T1): the ones it may do. */
export const frontDeskTodoKinds: readonly OwnerTodoKind[] = ["pending_members"];

export const ownerTodoItemSchema = z.object({
  kind: ownerTodoKindSchema,
  /** How many things wait in this job (answers, people, sessions, names). Always 1 or more. */
  count: z.number().int().min(1),
  /** The first names behind the job, oldest first, for the line under the title. At most 3. */
  names: z.array(z.string()).max(3),
  /** When the oldest thing in this job started to wait. `plan_next_month`: the last planned session. */
  since: isoDateTimeSchema,
  /** `plan_next_month`: the month to plan, `yyyy-MM`. */
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
});
export type OwnerTodoItem = z.infer<typeof ownerTodoItemSchema>;

/** `GET /business/businesses/:id/todo`: the jobs in the order the owner should do them. */
export const ownerTodoSchema = z.object({ items: z.array(ownerTodoItemSchema) });
export type OwnerTodo = z.infer<typeof ownerTodoSchema>;

/**
 * `GET /business/businesses/:id/home/open-spots` (Studio Home H2): the group sessions of the next
 * 48 hours with free places, not private, not cancelled, each with the Book link of the session.
 * A session with no limit has no count of free places, so it is not in the list.
 */
export const ownerOpenSpotSchema = z.object({
  id: uuidSchema,
  groupId: uuidSchema,
  groupName: z.string(),
  groupColor: z.string().nullable(),
  startsAt: isoDateTimeSchema,
  capacity: z.number().int(),
  booked: z.number().int(),
  /** Free places: the capacity less the bookings. Always 1 or more. */
  open: z.number().int().min(1),
  /** The Book page of the session on Desk, with `entry=open_spots` (`memberBookPath`). */
  bookUrl: z.string(),
});
export type OwnerOpenSpot = z.infer<typeof ownerOpenSpotSchema>;

export const ownerOpenSpotsSchema = z.object({
  /** The window, in hours from now. */
  windowHours: z.number().int(),
  /** Free places in every session of the list. */
  spots: z.number().int().min(0),
  /** In time order. */
  sessions: z.array(ownerOpenSpotSchema),
});
export type OwnerOpenSpots = z.infer<typeof ownerOpenSpotsSchema>;
